import axiosNest from '../../api/axiosNest';

/**
 * Decyzje Administracji w oknie kontroli — klient API (26.10.2026, makieta
 * p. Mariusza z 21.10; przełączniki `intakeControlDecisions` S-26.10-06
 * i `intakeControlPriceSurcharges` S-26.10-07; reguły API:
 * `apps/api/src/intake/intake-decisions.ts`). Lista, odcisk danych i zdania
 * odmowy przychodzą z API — front nie liczy ich sam.
 */

/** Klucze przełączników — dane z rejestru API, nie przemianowujemy. */
export const FEATURE_INTAKE_CONTROL_DECISIONS = 'intakeControlDecisions';
export const FEATURE_INTAKE_CONTROL_PRICE_SURCHARGES = 'intakeControlPriceSurcharges';

export type DecisionKind = 'company' | 'vat' | 'price' | 'surcharge';

export interface DecisionView {
  id: number;
  outcome: string;
  outcomeLabel: string;
  value: string | null;
  reason: string | null;
  details: Record<string, unknown> | null;
  decidedBy: string | null;
  decidedAt: string;
  stale: boolean;
}

export interface DecisionItem {
  subject: string;
  kind: DecisionKind;
  label: string;
  title: string;
  current: string | null;
  hint: string;
  state: 'open' | 'stale' | 'done';
  basis: string;
  decision: DecisionView | null;
  canConfirm: boolean;
  confirmRefusal: string | null;
}

export interface DecisionHistoryEntry {
  id: number;
  subject: string;
  outcome: string;
  outcomeLabel: string;
  value: string | null;
  reason: string | null;
  details: Record<string, unknown> | null;
  decidedBy: string | null;
  decidedAt: string;
  supersededAt: string | null;
}

export interface IntakeDecisions {
  items: DecisionItem[];
  pending: number;
  canDecide: boolean;
  decideRefusal: string | null;
  exportRefusal: string | null;
  priceAndSurcharges: boolean;
  pricelistNote: string;
  history: DecisionHistoryEntry[];
}

/** Stawki VAT do wyboru przy „Inna…” — jak w API (`VAT_RATES`). */
export const VAT_RATES = ['23%', '19%', '0%', 'NP'] as const;

/** Skąd stawka SPOT — te same rodzaje co źródło ustalenia (dane). */
export const SPOT_CHANNELS: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'phone', name: 'telefon' },
  { id: 'email', name: 'mail poza systemem' },
  { id: 'verbal', name: 'ustalenie ustne' },
  { id: 'other', name: 'inne' },
];

export const MIN_DECISION_REASON = 10;

export type DecisionBody =
  | { subject: string; basis: string; outcome: 'confirmed' }
  | { subject: string; basis: string; outcome: 'exception'; companyId: number; reason: string }
  | { subject: string; basis: string; outcome: 'exception'; rate: string; reason: string }
  | { subject: string; basis: string; outcome: 'rejected'; reason: string }
  | { subject: string; basis: string; outcome: 'confirmed'; priceSource: 'document'; document: string }
  | {
      subject: string;
      basis: string;
      outcome: 'confirmed';
      priceSource: 'spot';
      spotBy: string;
      spotChannel: string;
      spotAt: string;
      spotNote?: string;
    };

export async function postDecision(preOrderId: number | string, body: DecisionBody): Promise<IntakeDecisions> {
  const { data } = await axiosNest.post<IntakeDecisions>(`/intake/pre-orders/${preOrderId}/decisions`, body);
  return data;
}

/** Tematy z historii po ludzku. */
export function subjectLabel(subject: string, items: readonly DecisionItem[]): string {
  const item = items.find((i) => i.subject === subject);
  if (item) return item.label;
  if (subject === 'company') return 'Spółka';
  if (subject === 'vat') return 'Stawka VAT';
  if (subject === 'price') return 'Cena';
  if (subject.startsWith('surcharge:')) return 'Dodatek';
  return subject;
}
